//https://learn.microsoft.com/en-us/aspnet/signalr/overview/guide-to-the-api/hubs-api-guide-javascript-client#manualproxy
//https://learn.microsoft.com/en-us/aspnet/signalr/overview/guide-to-the-api/handling-connection-lifetime-events
import { createDebugger } from '@/_core/utils/debug';
const debug = createDebugger('fluxstore:realtimeService');

/**
 * Deserializes date strings in the received action object.
 * Looks for common date field patterns and converts ISO date strings back to Date objects.
 */
function deserializeDateFields(obj: any): void {
    if (!obj || typeof obj !== 'object') return;

    // List of field names that should be Date objects
    const dateFieldNames = [
        // Card-related dates
        'createdAt',
        'lastUpdatedAt',
        'updatedAt',
        'deletedAt',
        'archivedAt',
        'modifiedAt',
        'completedAt',
        
        // Release-related dates
        'startDate',
        'releaseDate',
        'targetDate',
        'dueDate',
        'startedAt',
        'endedAt',
        
        // Filter-related dates
        'updatedDateFrom',
        'updatedDateTo',
        
        // Comment/notification dates
        'CommentedAt',
        'commentCreatedAt',
        'publishedAt',
        
        // Account/API dates
        'apiTokenGeneratedAt',
        
        // Generic update tracking
        'updateAt',
        '_updateAt',
        '_commentCreatedAt'
    ];

    // ISO date string pattern
    const isoDatePattern = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(\.\d{3})?Z?$/;

    function processObject(item: any): void {
        if (!item || typeof item !== 'object') return;
        
        // Check each property
        for (const key in item) {
            if (!item.hasOwnProperty(key)) continue;
            
            const value = item[key];
            
            // Check if this field name suggests it's a date
            if (dateFieldNames.includes(key) && typeof value === 'string' && isoDatePattern.test(value)) {
                item[key] = new Date(value);
            }
            // Recursively process nested objects and arrays
            else if (value && typeof value === 'object') {
                if (Array.isArray(value)) {
                    value.forEach(processObject);
                } else {
                    processObject(value);
                }
            }
        }
    }

    // Process the main object and any nested properties
    processObject(obj);
    
    // Special handling for _savedCard or other known nested structures
    if (obj._savedCard) {
        processObject(obj._savedCard);
    }
    if (obj.cards && Array.isArray(obj.cards)) {
        obj.cards.forEach(processObject);
    }
}

export const createRealTimeConnection = (correlationId: string, storeType: string) => {
    let isConnected = false;
    let tryingToReconnect = false;
    let connectionStatus = 'disconnected';

    let donotSentBecouseNotConnectedQueue: any[] = [];
    let isSendFlush = false;

    type subFn = (obj: any) => void;
    const subs: subFn[] = [];

    const connectionStatusChangeCallbacks: ((status: string) => void)[] = [];

    function onConnectionStatusChange(callback: (status: string) => void) {
        connectionStatusChangeCallbacks.push(callback);
    }

    //@ts-ignore
    const connection = $.hubConnection('/pushendpoint', {
        useDefaultPath: false,
    });
    connection.qs = {
        correlationId: correlationId,
        storeType: storeType,
    };

    connection.logging = false;
    const fuxStoreHube = connection.createHubProxy('fluxstore');

    function connect() {
        connection.start({}).done(() => {
            debug('-----------Connected!!!-------');
            isConnected = true;
            flushDoNotSentBecouseNotConnectedQueue();
        });
    }

    function disConnect() {
        connection.stop();
    }

    connection.reconnecting(function() {
        tryingToReconnect = true;
    });

    connection.reconnected(function() {
        isConnected = true;
        tryingToReconnect = false;
    });

    connection.disconnected(function() {
        isConnected = false;
        debug('-----------DisConnected!!!-------');
        if (tryingToReconnect) {
            setTimeout(() => {
                connect();
            }, 500);
        }
    });

    connection.stateChanged(function(state: any) {
        switch (state.newState) {
            //@ts-ignore
            case $.signalR.connectionState.connecting:
                connectionStatus = 'connecting';
                break;
            //@ts-ignore
            case $.signalR.connectionState.connected:
                connectionStatus = 'connected';
                break;
            //@ts-ignore
            case $.signalR.connectionState.reconnecting:
                connectionStatus = 'connecting';
                break;
            //@ts-ignore
            case $.signalR.connectionState.disconnected:
                //its connecting because we are trying to reconnect and the state is continuously fluctuating between reconnecting and disconnected
                connectionStatus = 'connecting';
                break;
            default:
                connectionStatus = 'disconnected';
                break;
        }

        connectionStatusChangeCallbacks.forEach((callback) => callback(connectionStatus));
    });

    // connection.stateChanged(function(oldState: any, newState: any) {
    //     debug('-----------stateChanged!!!-------', oldState, newState);
    // });

    // connection.connectionSlow(function() {
    //     debug('We are currently experiencing difficulties with the connection.');
    // });

    function subcribe(fn: (obj: any) => void): void {
        subs.push(fn);
    }

    fuxStoreHube.on('notify', (json: string) => {
        const obj = JSON.parse(json);
        
        // Deserialize date strings back to Date objects
        deserializeDateFields(obj);
        
        subs.forEach((fn) => {
            fn(obj);
        });
        debug('[Realtime fluxstore]: ', obj);
    });

    function send(obj: any) {
        if (isConnected && !isSendFlush) {
            fuxStoreHube.invoke('send', JSON.stringify(obj)).done(() => {
                //debug("It's gone!!!!!!!!!!!");
            });
        } else {
            donotSentBecouseNotConnectedQueue.push(obj);
        }
    }

    function flushDoNotSentBecouseNotConnectedQueue() {
        isSendFlush = true;
        donotSentBecouseNotConnectedQueue.forEach((obj: any) => {
            fuxStoreHube.invoke('send', JSON.stringify(obj)).done(() => {
                //debug('It\'s gone from queue!!!!');
            });
        });
        donotSentBecouseNotConnectedQueue = [];
        isSendFlush = false;
    }

    return {
        send,
        subcribe,
        connect,
        disConnect,
        isConnected,
        onConnectionStatusChange,
    };
};
